import React, { useEffect, useState } from 'react';
import axios from 'axios';
import '../styles/AdminSettings.css';

const AdminSettings = () => {
	const [categories, setCategories] = useState([]);
	const [newCategoryName, setNewCategoryName] = useState('');
	const [newSubCategoryName, setNewSubCategoryName] = useState('');
	const [expandedCategoryId, setExpandedCategoryId] = useState(null);
	const [colors, setColors] = useState([]);
	const [loading, setLoading] = useState(false);
	const [message, setMessage] = useState('');

	useEffect(() => {
		fetchCategories();
		fetchColors();
	}, []);

	// Auto-clear message after 3 seconds
	useEffect(() => {
		if (message) {
			const timer = setTimeout(() => setMessage(''), 3000);
			return () => clearTimeout(timer);
		}
	}, [message]);

	const fetchCategories = async () => {
		try {
			const [catRes, prodRes] = await Promise.all([
				axios.get(`${process.env.REACT_APP_API_URL}/api/categories`),
				axios.get(`${process.env.REACT_APP_API_URL}/api/products`)
			]);

			const products = prodRes.data || [];

			// Count products per category and subcategory
			const categoriesWithCounts = (catRes.data || []).map(cat => {
				const productCount = products.filter(p =>
					Array.isArray(p.categories) &&
					p.categories.some(c => String(c.category) === String(cat._id))
				).length;

				const subcategoriesWithCounts = (cat.subcategories || []).map(subcat => {
					const subCategoryCount = products.filter(p =>
						Array.isArray(p.categories) &&
						p.categories.some(c =>
							String(c.category) === String(cat._id) &&
							c.subCategory === subcat.name
						)
					).length;

					return { ...subcat, productCount: subCategoryCount };
				});

				return { ...cat, productCount, subcategories: subcategoriesWithCounts };
			});

			setCategories(categoriesWithCounts);
		} catch (error) {
			console.error('Error fetching categories:', error);
			setMessage('Błąd pobierania danych');
		}
	};

	const fetchColors = async () => {
		try {
			const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/colors`);
			setColors(res.data || []);
		} catch (error) {
			console.error("Error fetching colors:", error);
			setMessage("❌ Błąd pobierania kolorów");
		}
	};

	const handleAddCategory = async (e) => {
		e.preventDefault();
		if (!newCategoryName.trim()) {
			setMessage('Nazwa kategorii nie może być pusta');
			return;
		}

		setLoading(true);
		try {
			await axios.post(`${process.env.REACT_APP_API_URL}/api/categories`, {
				name: newCategoryName
			});
			setMessage('✅ Kategoria dodana');
			setNewCategoryName('');
			await fetchCategories();
		} catch (error) {
			console.error('Error adding category:', error);
			setMessage('❌ Błąd dodawania kategorii');
		} finally {
			setLoading(false);
		}
	};

	const handleAddSubCategory = async (categoryId) => {
		if (!newSubCategoryName.trim()) {
			setMessage('Nazwa podkategorii nie może być pusta');
			return;
		}

		setLoading(true);
		try {
			await axios.post(
				`${process.env.REACT_APP_API_URL}/api/categories/${categoryId}/subcategories`,
				{ name: newSubCategoryName }
			);
			setMessage('✅ Podkategoria dodana');
			setNewSubCategoryName('');
			await fetchCategories();
		} catch (error) {
			console.error('Error adding subcategory:', error);
			setMessage('❌ Błąd dodawania podkategorii');
		} finally {
			setLoading(false);
		}
	};

	const handleDeleteCategory = async (categoryId) => {
		if (!window.confirm('Na pewno usunąć tę kategorię?')) return;

		setLoading(true);
		try {
			await axios.delete(`${process.env.REACT_APP_API_URL}/api/categories/${categoryId}`, {
				headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
			});
			setMessage('✅ Kategoria usunięta');
			await fetchCategories();
		} catch (error) {
			console.error('Error deleting category:', error);
			setMessage('❌ Błąd usuwania kategorii');
		} finally {
			setLoading(false);
		}
	};

	const handleDeleteSubCategory = async (categoryId, subCategoryName) => {
		if (!window.confirm('Na pewno usunąć tę podkategorię?')) return;

		setLoading(true);
		try {
			await axios.delete(
				`${process.env.REACT_APP_API_URL}/api/categories/${categoryId}/subcategories/${subCategoryName}`,
				{ headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` } }
			);
			setMessage('✅ Podkategoria usunięta');
			await fetchCategories();
		} catch (error) {
			console.error('Error deleting subcategory:', error);
			setMessage('❌ Błąd usuwania podkategorii');
		} finally {
			setLoading(false);
		}
	};

	const handleDeleteColor = async (colorId) => {
		if (!window.confirm("Na pewno usunąć ten kolor?")) return;

		setLoading(true);
		try {
			await axios.delete(`${process.env.REACT_APP_API_URL}/api/colors/${colorId}`, {
				headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
			});
			setMessage("✅ Kolor usunięty");
			await fetchColors();
		} catch (error) {
			console.error("Error deleting color:", error);
			setMessage("❌ Błąd usuwania koloru");
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className="admin-settings">
			{/* Global Message */}
			{message && (
				<div className={`message ${message.includes('✅') ? 'success' : 'error'}`}>
					{message}
				</div>
			)}

			{/* Categories Section */}
			<div className="categories-section">
				<h2>Zarządzanie Kategoriami</h2>

				<div className="settings-section">
					<h3>Dodaj nową kategorię</h3>
					<form onSubmit={handleAddCategory} className="add-category-form">
						<input
							type="text"
							placeholder="Nazwa kategorii"
							value={newCategoryName}
							onChange={(e) => setNewCategoryName(e.target.value)}
							disabled={loading}
						/>
						<button type="submit" disabled={loading}>
							{loading ? 'Dodawanie...' : '➕ Dodaj kategorię'}
						</button>
					</form>
				</div>

				<div className="settings-section">
					<h3>Kategorie ({categories.length})</h3>
					{categories.length === 0 ? (
						<p>Brak kategorii</p>
					) : (
						<div className="categories-list">
							{categories.map((category) => (
								<div key={category._id} className="category-item">
									<div
										className="category-header"
										onClick={() =>
											setExpandedCategoryId(
												expandedCategoryId === category._id ? null : category._id
											)
										}
									>
										<span className="expand-btn">
											{expandedCategoryId === category._id ? '▼' : '▶'}
										</span>
										<span className="category-name">{category.name}</span>
										<span className="category-count">
											({category.productCount || 0} produktów)
										</span>
										<button
											className="delete-btn"
											onClick={(e) => {
												e.stopPropagation();
												handleDeleteCategory(category._id);
											}}
											disabled={loading}
										>
											🗑️ Usuń
										</button>
									</div>

									{expandedCategoryId === category._id && (
										<div className="category-content">
											<div className="subcategories-section">
												<h4>Podkategorie ({category.subcategories?.length || 0})</h4>
												{category.subcategories?.length > 0 ? (
													<div className="subcategories-list">
														{category.subcategories.map((subcat) => (
															<div key={subcat._id || subcat.name} className="subcategory-item">
																<div className="subcategory-info">
																	<span>{subcat.name}</span>
																	<span className="subcat-count">
																		({subcat.productCount || 0})
																	</span>
																</div>
																<button
																	className="delete-btn"
																	onClick={() => handleDeleteSubCategory(category._id, subcat.name)}
																	disabled={loading}
																>
																	🗑️
																</button>
															</div>
														))}
													</div>
												) : (
													<p>Brak podkategorii</p>
												)}
											</div>

											<div className="add-subcategory-section">
												<input
													type="text"
													placeholder="Nowa podkategoria"
													value={newSubCategoryName}
													onChange={(e) => setNewSubCategoryName(e.target.value)}
													disabled={loading}
												/>
												<button
													onClick={() => handleAddSubCategory(category._id)}
													disabled={loading}
												>
													{loading ? 'Dodawanie...' : '➕ Dodaj'}
												</button>
											</div>
										</div>
									)}
								</div>
							))}
						</div>
					)}
				</div>
			</div>

			{/* Colors Section */}
			<div className="colors-section">
				<h2>Zarządzanie Kolorami</h2>
				<div className="settings-section">
					<h3>Lista Kolorów ({colors.length})</h3>
					{colors.length === 0 ? (
						<p>Brak kolorów</p>
					) : (
						<div className="colors-list">
							{colors.map((color) => (
								<div key={color._id} className="color-item">
									<div className="color-info">
										<span 
											className="color-preview" 
											style={{ backgroundColor: color.hex || '#ccc', width: '20px', height: '20px', display: 'inline-block', marginRight: '10px', borderRadius: '50%' }}
										></span>
										<span>{color.name}</span>
									</div>
									<button
										className="delete-btn"
										onClick={() => handleDeleteColor(color._id)}
										disabled={loading}
									>
										🗑️ Usuń
									</button>
								</div>
							))}
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default AdminSettings;
